import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/src/contexts/AuthContext';
import { useSearch } from '@/src/contexts/SearchContext';
import { db, collection, addDoc, serverTimestamp } from '@/firebase';
import { runAnalysis, getLocationSuggestions, geocodeLocation } from '@/src/services/apiServices';
import { BusinessIdea, ComparisonAnalysis } from '@/src/types';
import { generateBusinessSnapshot, generateComparisonReport } from '@/src/services/pdfService';

export function useReporting() {
  const { user, isLoggedIn, userProfile, isEnterprise } = useAuth();
  const { state } = useSearch();
  const hasReported = useRef(false);

  useEffect(() => {
    if (!isLoggedIn || !user || hasReported.current) return;

    const reportScan = async () => {
      try {
        if (!state.result || !state.activeFeature) return;

        const avgScore = state.result.ideas?.length > 0 
          ? Math.round(state.result.ideas.reduce((acc: number, idea: any) => acc + (idea.marketDemandScore || 0), 0) / state.result.ideas.length)
          : 0;

        await addDoc(collection(db, 'users', user.uid, 'reports'), {
          location: state.launchpadLocation || state.discoveryLocation || 'Global',
          sector: state.launchpadSector || state.densitySector || 'General',
          type: state.activeFeature,
          score: avgScore,
          currency: state.preferredCurrency,
          timestamp: serverTimestamp()
        });

        hasReported.current = true;
      } catch (e) {
        console.error('Failed to report scan:', e);
      }
    };

    reportScan();
  }, [state.result, state.activeFeature, isLoggedIn, user]);

  const getBranding = useCallback(() => {
    if (!isEnterprise) return undefined;
    return {
      logo: userProfile?.photoURL,
      name: userProfile?.name
    };
  }, [isEnterprise, userProfile]);

  const exportBusinessSnapshot = useCallback(async (idea?: BusinessIdea) => {
    console.log('[useReporting] exportBusinessSnapshot called', idea);
    if (!idea) return;
    if (!userProfile?.subscriptionTier || userProfile.subscriptionTier === 'free') {
      window.dispatchEvent(new CustomEvent('trigger-upgrade'));
      return;
    }
    const branding = getBranding();
    await generateBusinessSnapshot(idea, branding);
  }, [getBranding, userProfile]);

  const exportComparisonReport = useCallback(async (ideas?: BusinessIdea[], comparison?: ComparisonAnalysis) => {
    console.log('[useReporting] exportComparisonReport called', ideas, comparison);
    if (!ideas) return;
    if (!userProfile?.subscriptionTier || userProfile.subscriptionTier === 'free') {
      window.dispatchEvent(new CustomEvent('trigger-upgrade'));
      return;
    }
    const branding = getBranding();
    await generateComparisonReport(ideas, comparison, branding);
  }, [getBranding, userProfile]);

  // RETURN AN OBJECT so App.tsx can destructure from it
  return {
    exportBusinessSnapshot,
    exportComparisonReport
  };
}
